import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Chip, Divider,
  Radio, RadioGroup, FormControlLabel, CircularProgress, Alert, TextField,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import * as XLSX from "xlsx";
import { MOCK_ZONAS, MOCK_TECNOLOGIAS } from "../../api/mockCalidadDatos";
import { axiosPrivate } from "../../api/axios";
import PageBody from "../GeneralComponents/PageBody";
import InputFilter from "../GeneralComponents/InputFilter";

const OP_COLORS = [
  "#4CAF50", "#1976D2", "#FF9800", "#9C27B0", "#795548",
  "#F44336", "#42A5F5", "#263238", "#388E3C", "#00897B",
  "#FF5722", "#78909C",
];

const METRICS = [
  {
    key: "subida",
    label: "Tráfico promedio de subida",
    unit: "Mb/s",
    description: "Evolución del tráfico promedio de subida por operador en la ventana seleccionada.",
    convert: (v) => (v * 8) / 1e6,
  },
  {
    key: "bajada",
    label: "Tráfico promedio de bajada",
    unit: "Mb/s",
    description: "Evolución del tráfico promedio de bajada por operador en la ventana seleccionada.",
    convert: (v) => (v * 8) / 1e6,
  },
  {
    key: "interac_bajada",
    label: "Tráfico total de bajada internacional",
    unit: "Mb/s",
    description: "Evolución del tráfico de bajada internacional agregado por operador.",
    convert: (v) => (v * 8) / 1e6,
  },
  {
    key: "latencia",
    label: "Latencia",
    unit: "ms",
    description: "Evolución de la latencia promedio por operador en la ventana seleccionada.",
    convert: (v) => v,
  },
  {
    key: "perdida_paquetes",
    label: "Pérdida de paquetes",
    unit: "%",
    description: "Evolución de la pérdida de paquetes promedio por operador en la ventana seleccionada.",
    convert: (v) => v,
  },
];

const DIMENSION_OPTIONS = [
  { key: "rbd",          label: "RBD"      },
  { key: "zona",         label: "Zona"     },
  { key: "operador",     label: "Oper."    },
  { key: "global",       label: "Global"   },
  { key: "zona_tec",     label: "Zona/Tec" },
  { key: "operador_tec", label: "Op/Tec"   },
  { key: "global_tec",   label: "Glob/Tec" },
];

const toDateStr     = (d) => d.toISOString().split("T")[0];
const daysAgo       = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return toDateStr(d); };
const monthLastDay  = (ym) => { const [y, m] = ym.split("-").map(Number); return toDateStr(new Date(y, m, 0)); };
const monthsAgoFirst = (n) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - n); return toDateStr(d); };

const GRAN_CONFIG = {
  "15m": {
    label: "15 min",
    presets: [
      { label: "Hoy",  compute: () => { const t = toDateStr(new Date()); return { ini: t, fin: t }; } },
      { label: "Ayer", compute: () => { const t = daysAgo(1);            return { ini: t, fin: t }; } },
    ],
    getDefaultRange: () => { const t = toDateStr(new Date()); return { ini: t, fin: t }; },
    apiUrl: "/service/reportes/supervision/cq",
    apiGran: "15m",
  },
  "1d": {
    label: "1 día",
    presets: [
      { label: "3 días",   compute: () => ({ ini: daysAgo(3),  fin: toDateStr(new Date()) }) },
      { label: "1 semana", compute: () => ({ ini: daysAgo(7),  fin: toDateStr(new Date()) }) },
      { label: "1 mes",    compute: () => ({ ini: daysAgo(30), fin: toDateStr(new Date()) }) },
      { label: "3 meses",  compute: () => ({ ini: daysAgo(90), fin: toDateStr(new Date()) }) },
    ],
    getDefaultRange: () => ({ ini: daysAgo(30), fin: toDateStr(new Date()) }),
    apiUrl: "/service/reportes/stats-rbd/agregado",
    apiGran: "1d",
  },
  "7d": {
    label: "7 días",
    presets: [
      { label: "1 mes",    compute: () => ({ ini: monthsAgoFirst(1), fin: toDateStr(new Date()) }) },
      { label: "3 meses",  compute: () => ({ ini: monthsAgoFirst(3), fin: toDateStr(new Date()) }) },
      { label: "6 meses",  compute: () => ({ ini: monthsAgoFirst(6), fin: toDateStr(new Date()) }) },
    ],
    getDefaultRange: () => ({ ini: monthsAgoFirst(3), fin: toDateStr(new Date()) }),
    apiUrl: "/service/reportes/stats-rbd/agregado",
    apiGran: "7d",
  },
  "30d": {
    label: "1 mes",
    presets: [
      { label: "1 mes",    compute: () => ({ ini: monthsAgoFirst(1), fin: toDateStr(new Date()) }) },
      { label: "3 meses",  compute: () => ({ ini: monthsAgoFirst(3), fin: toDateStr(new Date()) }) },
      { label: "6 meses",  compute: () => ({ ini: monthsAgoFirst(6), fin: toDateStr(new Date()) }) },
    ],
    getDefaultRange: () => ({ ini: monthsAgoFirst(6), fin: toDateStr(new Date()) }),
    apiUrl: "/service/reportes/stats-rbd/agregado",
    apiGran: "30d",
  },
};

function buildChartData(filteredRows, metric, ventana, getKey) {
  const colName = `${metric.key}_${ventana}_mean`;

  const byDate = {};
  filteredRows.forEach((r) => {
    const k = getKey(r);
    if (!byDate[r.fecha]) byDate[r.fecha] = {};
    if (!byDate[r.fecha][k]) byDate[r.fecha][k] = [];
    if (r[colName] != null && !isNaN(r[colName])) {
      byDate[r.fecha][k].push(r[colName]);
    }
  });
  return Object.entries(byDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([fecha, entityData]) => {
      const row = { time: fecha.includes("T") ? fecha.slice(11, 16) : fecha };
      Object.entries(entityData).forEach(([k, vals]) => {
        const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
        row[k] = Number(metric.convert(mean).toFixed(4));
      });
      return row;
    });
}

function exportXlsx(filteredRows, metric, ventana, operators) {
  const colName = `${metric.key}_${ventana}_mean`;
  const data = filteredRows
    .filter((r) => r[colName] != null)
    .map((r) => {
      const op = operators.find((o) => o.id_operador === r.id_operador);
      return {
        fecha: r.fecha,
        operador: op?.nombre_operador ?? `Op ${r.id_operador}`,
        [`${metric.key} (${metric.unit})`]: Number(metric.convert(r[colName]).toFixed(4)),
      };
    });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, metric.key);
  XLSX.writeFile(wb, `trafico_${metric.key}_${ventana}.xlsx`);
}

const downloadBtnSx = {
  border: "2px solid #003B8D",
  borderRadius: "4px",
  color: "#003B8D",
  backgroundColor: "#FFF",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 16px",
  fontWeight: 500,
  "&:hover": { backgroundColor: "#f0f4ff" },
};

export default function TendenciasMetricas() {
  const [operators,    setOperators]    = useState([]);
  const [apiData,      setApiData]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);

  const [granularity,  setGranularity]  = useState("1d");
  const [fechaInicio,  setFechaInicio]  = useState(() => daysAgo(30));
  const [fechaFin,     setFechaFin]     = useState(() => toDateStr(new Date()));
  const [selectedOps,  setSelectedOps]  = useState([]);
  const [selectedZona, setSelectedZona] = useState(null);
  const [selectedRbd,  setSelectedRbd]  = useState(null);
  const [selectedTec,  setSelectedTec]  = useState(null);
  const [ventana,           setVentana]           = useState("dia");
  const [selectedDimension, setSelectedDimension] = useState("operador");

  // Load operators once
  useEffect(() => {
    axiosPrivate({ method: "GET", url: "/service/admin/get_operadores/", showSnackbarOnError: false })
      .then((res) => {
        const ops = res.data ?? [];
        setOperators(ops);
        setSelectedOps(ops.map((o) => o.id_operador));
      })
      .catch(() => {});
  }, []);

  const granConfig = GRAN_CONFIG[granularity];

  // Fetch stats when granularity, dates or filters change
  useEffect(() => {
    const cfg = GRAN_CONFIG[granularity];
    setLoading(true);
    setError(null);
    axiosPrivate({
      method: "GET",
      url: cfg.apiUrl,
      params: {
        granularidad: cfg.apiGran,
        dimension: selectedDimension,
        fecha_inicio: fechaInicio,
        fecha_fin:    fechaFin,
        ...(selectedZona != null && selectedZona !== "all" ? { id_zona:        Number(selectedZona) } : {}),
        ...(selectedRbd  != null && selectedRbd  !== "all" ? { rbd:             selectedRbd }         : {}),
        ...(selectedTec  != null && selectedTec  !== "all" ? { tipo_tecnologia: Number(selectedTec) } : {}),
        ...(selectedOps.length === 1                       ? { id_operador:     selectedOps[0] }      : {}),
      },
      showSnackbarOnError: false,
    })
      .then((res) => setApiData((res.data ?? []).map((r) => ({ ...r, fecha: r.fecha_bucket }))))
      .catch(() => setError("Error al cargar los datos."))
      .finally(() => setLoading(false));
  }, [granularity, fechaInicio, fechaFin, selectedZona, selectedRbd, selectedTec, selectedOps, selectedDimension]);

  const getEntityKey = useCallback((r) => {
    const tec = r.tipo_tecnologia != null ? String(r.tipo_tecnologia) : "";
    switch (selectedDimension) {
      case "zona":         return String(r.id_zona ?? "");
      case "zona_tec":     return `${r.id_zona ?? ""}|${tec}`;
      case "rbd":          return String(r.rbd ?? "");
      case "operador":     return String(r.id_operador ?? "");
      case "operador_tec": return `${r.id_operador ?? ""}|${tec}`;
      case "global_tec":   return tec;
      default:             return "global";
    }
  }, [selectedDimension]);

  const getEntityLabel = useCallback((key) => {
    switch (selectedDimension) {
      case "zona":
        return MOCK_ZONAS.find((z) => String(z.id) === key)?.label ?? `Zona ${key}`;
      case "zona_tec": {
        const [zonaId, tecId] = key.split("|");
        const zonaLabel = MOCK_ZONAS.find((z) => String(z.id) === zonaId)?.label ?? `Zona ${zonaId}`;
        const tecLabel  = MOCK_TECNOLOGIAS.find((t) => String(t.id) === tecId)?.label ?? `Tec ${tecId}`;
        return `${zonaLabel} / ${tecLabel}`;
      }
      case "rbd":
        return `RBD ${key}`;
      case "operador":
        return operators.find((o) => String(o.id_operador) === key)?.nombre_operador ?? key;
      case "operador_tec": {
        const [opKey, tecId] = key.split("|");
        const opLabel  = operators.find((o) => String(o.id_operador) === opKey)?.nombre_operador ?? opKey;
        const tecLabel = MOCK_TECNOLOGIAS.find((t) => String(t.id) === tecId)?.label ?? `Tec ${tecId}`;
        return `${opLabel} / ${tecLabel}`;
      }
      case "global_tec":
        return MOCK_TECNOLOGIAS.find((t) => String(t.id) === key)?.label ?? `Tec ${key}`;
      default:
        return "Global";
    }
  }, [selectedDimension, operators]);

  const filteredRows = useMemo(() => {
    if (granularity === "15m") return apiData;
    if (!selectedDimension.startsWith("operador")) return apiData;
    if (!selectedOps.length) return apiData;
    return apiData.filter((r) => selectedOps.includes(r.id_operador));
  }, [apiData, selectedOps, selectedDimension, granularity]);

  const entityKeys = useMemo(
    () => [...new Set(filteredRows.map(getEntityKey).filter(Boolean))],
    [filteredRows, getEntityKey]
  );

  const entityColorMap = useMemo(() => {
    const m = {};
    entityKeys.forEach((k, i) => { m[k] = OP_COLORS[i % OP_COLORS.length]; });
    return m;
  }, [entityKeys]);

  const maxFechaFin = useMemo(() => {
    if (granularity !== "1d") return toDateStr(new Date());
    const cap = new Date(fechaInicio + "T12:00:00");
    cap.setMonth(cap.getMonth() + 6);
    const today = new Date();
    return cap > today ? toDateStr(today) : toDateStr(cap);
  }, [granularity, fechaInicio]);

  const handleClearFilters = useCallback(() => {
    const { ini, fin } = GRAN_CONFIG["1d"].getDefaultRange();
    setGranularity("1d");
    setFechaInicio(ini);
    setFechaFin(fin);
    setSelectedOps(operators.map((o) => o.id_operador));
    setSelectedZona(null);
    setSelectedRbd(null);
    setSelectedTec(null);
    setVentana("dia");
    setSelectedDimension("operador");
  }, [operators]);

  const operadorOpts = useMemo(() => [
    { value: "all", label: "Todos los operadores" },
    ...operators.map((o) => ({ value: String(o.id_operador), label: o.nombre_operador })),
  ], [operators]);

  const opFilterValue = !selectedOps.length || selectedOps.length === operators.length
    ? "all"
    : String(selectedOps[0] ?? "all");

  const opColorMap = useMemo(() => {
    const m = {};
    operators.forEach((o, i) => { m[o.id_operador] = OP_COLORS[i % OP_COLORS.length]; });
    return m;
  }, [operators]);

  const [hiddenLines, setHiddenLines] = useState({});

  const handleLegendClick = useCallback((data) => {
    setHiddenLines((prev) => ({ ...prev, [data.dataKey]: !prev[data.dataKey] }));
  }, []);

  return (
    <PageBody>
      <Box sx={{ mx: "-3.5rem", display: "flex", gap: 2 }}>

        {/* ── Sidebar ── */}
        <Box sx={{ width: 300, flexShrink: 0, position: "sticky", top: 16, alignSelf: "flex-start" }}>
          <Paper sx={{
            p: 1.5,
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}>

            {/* Granularidad */}
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Granularidad</Typography>
            <Box sx={{ display: "flex", gap: 0, mb: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
              {Object.entries(GRAN_CONFIG).map(([key, cfg], i, arr) => (
                <Button key={key} size="small" disableRipple
                  variant={granularity === key ? "contained" : "text"}
                  color="primary"
                  onClick={() => {
                    const { ini, fin } = GRAN_CONFIG[key].getDefaultRange();
                    setGranularity(key);
                    setFechaInicio(ini);
                    setFechaFin(fin);
                  }}
                  sx={{ flex: 1, px: 0.3, py: 0.4, fontSize: "0.68rem", textTransform: "none", borderRadius: 0, borderRight: i < arr.length - 1 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                >
                  {cfg.label}
                </Button>
              ))}
            </Box>

            {/* Período */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>Período</Typography>

              {/* Atajos rápidos */}
              <Box sx={{ display: "flex", gap: 0, mb: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                {granConfig.presets.map((p, i) => {
                  const { ini, fin } = p.compute();
                  return (
                    <Button key={p.label} size="small" disableRipple
                      variant={fechaInicio === ini && fechaFin === fin ? "contained" : "text"}
                      color="primary"
                      onClick={() => { setFechaInicio(ini); setFechaFin(fin); }}
                      sx={{ flex: 1, px: 0.3, py: 0.4, fontSize: "0.68rem", textTransform: "none", borderRadius: 0, borderRight: i < granConfig.presets.length - 1 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                    >
                      {p.label}
                    </Button>
                  );
                })}
              </Box>

              {/* 15m: selector de un solo día */}
              {granularity === "15m" && (
                <TextField
                  label="Fecha" type="date" size="small" fullWidth
                  value={fechaInicio}
                  onChange={(e) => { setFechaInicio(e.target.value); setFechaFin(e.target.value); }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: toDateStr(new Date()) }}
                />
              )}

              {/* 1d: rango de fechas, máx 6 meses */}
              {granularity === "1d" && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    label="Desde" type="date" size="small" fullWidth
                    value={fechaInicio}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFechaInicio(v);
                      const cap = new Date(v + "T12:00:00");
                      cap.setMonth(cap.getMonth() + 6);
                      const today = new Date();
                      const maxFin = cap > today ? toDateStr(today) : toDateStr(cap);
                      if (fechaFin > maxFin) setFechaFin(maxFin);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: fechaFin }}
                  />
                  <TextField
                    label="Hasta" type="date" size="small" fullWidth
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: fechaInicio, max: maxFechaFin }}
                  />
                </Box>
              )}

              {/* 7d / 30d: selector por mes */}
              {(granularity === "7d" || granularity === "30d") && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    label="Desde" type="month" size="small" fullWidth
                    value={fechaInicio.slice(0, 7)}
                    onChange={(e) => setFechaInicio(e.target.value + "-01")}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: fechaFin.slice(0, 7) }}
                  />
                  <TextField
                    label="Hasta" type="month" size="small" fullWidth
                    value={fechaFin.slice(0, 7)}
                    onChange={(e) => {
                      const last = monthLastDay(e.target.value);
                      const today = toDateStr(new Date());
                      setFechaFin(last > today ? today : last);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: fechaInicio.slice(0, 7), max: toDateStr(new Date()).slice(0, 7) }}
                  />
                </Box>
              )}
            </Box>

            {/* Dimensión */}
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Dimensión</Typography>
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: "flex", gap: 0, border: "1px solid", borderColor: "divider", borderRadius: "4px 4px 0 0", overflow: "hidden", borderBottom: "none" }}>
                {DIMENSION_OPTIONS.slice(0, 4).map((d, i) => (
                  <Button key={d.key} size="small" disableRipple
                    variant={selectedDimension === d.key ? "contained" : "text"}
                    color="primary"
                    onClick={() => setSelectedDimension(d.key)}
                    sx={{ flex: 1, px: 0.3, py: 0.4, fontSize: "0.65rem", textTransform: "none", borderRadius: 0, borderRight: i < 3 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                  >
                    {d.label}
                  </Button>
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 0, border: "1px solid", borderColor: "divider", borderRadius: "0 0 4px 4px", overflow: "hidden" }}>
                {DIMENSION_OPTIONS.slice(4).map((d, i) => (
                  <Button key={d.key} size="small" disableRipple
                    variant={selectedDimension === d.key ? "contained" : "text"}
                    color="primary"
                    onClick={() => setSelectedDimension(d.key)}
                    sx={{ flex: 1, px: 0.3, py: 0.4, fontSize: "0.65rem", textTransform: "none", borderRadius: 0, borderRight: i < 2 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                  >
                    {d.label}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Operador */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter
                item="operador"
                name="Operador"
                opt={operadorOpts}
                value={opFilterValue}
                callback={(_, val) => {
                  if (!val || val === "all") setSelectedOps(operators.map((o) => o.id_operador));
                  else setSelectedOps([Number(val)]);
                }}
              />
            </Box>

            {/* Zona */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter
                item="zona"
                name="Zona"
                opt={[{ value: "all", label: "Todas las zonas" }, ...MOCK_ZONAS.map((z) => ({ value: String(z.id), label: z.label }))]}
                value={selectedZona}
                callback={(_, val) => setSelectedZona(val ?? null)}
              />
            </Box>

            {/* RBD */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter
                item="rbd"
                name="RBD"
                opt={[{ value: "all", label: "Todos los RBD" }]}
                value={selectedRbd}
                callback={(_, val) => setSelectedRbd(val ?? null)}
                noSort
              />
            </Box>

            {/* Tecnología */}
            <Box sx={{ mb: 1.5 }}>
              <InputFilter
                item="tecnologia"
                name="Tecnología"
                opt={[{ value: "all", label: "Todas las tecnologías" }, ...MOCK_TECNOLOGIAS.map((t) => ({ value: String(t.id), label: t.label }))]}
                value={selectedTec}
                callback={(_, val) => setSelectedTec(val ?? null)}
              />
            </Box>

            {/* Ventana */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Ventana</Typography>
              <RadioGroup value={ventana} onChange={(e) => setVentana(e.target.value)}>
                <FormControlLabel
                  value="ven"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Ventana Horaria</Typography>}
                />
                <FormControlLabel
                  value="dia"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Día completo</Typography>}
                />
              </RadioGroup>
            </Box>

            {/* Active filter chips */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
              {operators.filter((o) => selectedOps.includes(o.id_operador)).map((o) => (
                <Chip
                  key={o.id_operador}
                  label={o.nombre_operador}
                  size="small"
                  onDelete={() => setSelectedOps((prev) => prev.filter((x) => x !== o.id_operador))}
                  sx={{ fontSize: "0.68rem", bgcolor: "white", border: "1px solid #1976D2", "& .MuiChip-deleteIcon": { color: "#1976D2", "&:hover": { color: "#1565C0" } } }}
                />
              ))}
              {selectedTec != null && (
                <Chip
                  label={MOCK_TECNOLOGIAS.find((t) => String(t.id) === selectedTec)?.label}
                  size="small"
                  onDelete={() => setSelectedTec(null)}
                  sx={{ fontSize: "0.68rem", bgcolor: "white", border: "1px solid #1976D2", "& .MuiChip-deleteIcon": { color: "#1976D2", "&:hover": { color: "#1565C0" } } }}
                />
              )}
            </Box>

            {/* Clear */}
            <Button
              variant="outlined" color="error" size="small" fullWidth disableRipple
              startIcon={<RemoveCircleOutlineIcon />}
              onClick={handleClearFilters}
              sx={{ textTransform: "none" }}
            >
              Limpiar todos los filtros
            </Button>
          </Paper>
        </Box>

        {/* ── Charts ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
              <CircularProgress />
            </Box>
          ) : (
            METRICS.map((metric) => {
              const chartData = buildChartData(filteredRows, metric, ventana, getEntityKey);
              return (
                <Paper key={metric.key} sx={{ p: 3.5, mb: 2 }}>
                  {/* Fila 1: título (izquierda) + descripción (derecha) */}
                  <Grid2 container spacing={2} alignItems="flex-start" mb={3}>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                      <Typography
                        variant="h6" fontWeight="bold"
                        sx={{ textTransform: "uppercase", lineHeight: 1.3 }}
                      >
                        {metric.label}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 8 }}>
                      <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
                        {metric.description}
                      </Typography>
                    </Grid2>
                  </Grid2>

                  {/* Fila 2: gráfico ancho completo */}
                  <ResponsiveContainer width="100%" height={230}>
                    <LineChart data={chartData} margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="time"
                        tick={{ fontSize: 9 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 9 }}
                        tickFormatter={(v) => Number(v).toFixed(2)}
                        unit={` ${metric.unit}`}
                        width={70}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          `${Number(value).toFixed(2)} ${metric.unit}`,
                          getEntityLabel(name),
                        ]}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ fontSize: "10px", paddingTop: "6px", cursor: "pointer" }}
                        formatter={(value) => (
                          <span style={{ color: hiddenLines[value] ? "#bbb" : "inherit", textDecoration: hiddenLines[value] ? "line-through" : "none" }}>
                            {getEntityLabel(value)}
                          </span>
                        )}
                        onClick={handleLegendClick}
                      />
                      {entityKeys.map((k) => (
                        <Line
                          key={k}
                          type="monotone"
                          dataKey={k}
                          hide={hiddenLines[k] ?? false}
                          stroke={entityColorMap[k]}
                          dot={{ r: 3, fill: entityColorMap[k] }}
                          activeDot={{ r: 5 }}
                          strokeWidth={2}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>

                  <Divider sx={{ mt: 1.5, mb: 2 }} />

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={() => exportXlsx(filteredRows, metric, ventana, operators)} sx={downloadBtnSx}>
                      Descarga tabla (.csv)
                    </Button>
                  </Box>
                </Paper>
              );
            })
          )}
        </Box>

      </Box>
    </PageBody>
  );
}
